/* eslint-disable import/extensions */
/* eslint-disable import/no-unresolved */
import _ from 'underscore';
import { campaignMetricCategoriesFactory } from '@bingads-webui/custom-columns-shared-config';
import { RSAAdCustomizerAttributeColumn } from '@bingads-webui-campaign/rsa-ad-customizer-attribute-column';

import {
  BudgetColumn,
  LifetimeBudgetColumn,
  QualityScoreColumn,
  ColumnCTR,
  ColumnAverageCPC,
  ColumnAveragePosition,
  ColumnTopImpressionRate,
  ColumnAbsoluteTopImpressionRate,
  ColumnConversions,
  ColumnConversionsCredit,
  DeliveryColumn,
  ColumnClicksWithFluctuation,
  ColumnImpressionsWithFluctuation,
  ColumnSpendWithFluctuation,
  BidStrategyNameColumn,
  CampaignBidStrategyTypeColumn as BidStrategyTypeColumn,
  ColumnViewThroughConversionsCredit,
  ColumnViewThroughConversions,
} from '@bingads-webui-campaign/campaignui-common-columns';
import {
  CampaignCashbackPercentColumn,
  CashbackBudgetColumn,
  CampaignCashbackScopeColumn,
  CampaignCouponColumn,
} from '@bingads-webui-campaign/personalized-offer-columns';

import { LabelsColumn } from '@bingads-webui-campaign/labels';
import { ColumnsConfigBuilder } from '@bingads-webui-campaign/summary-grid-helper';
import config from '@bingads-webui-component-legacy/config/index';
import decimal from '@bingads-webui-component-legacy/humanize/decimal';
import { Columns, SelectedColumns } from '@bingads-webui-component-legacy/bingads-grid/service/column-chooser-preferences';
import { CustomColumns } from '@bingads-webui-component-legacy/bingads-grid/service/custom-column-preferences';
import { LegacyColumns, LegacySelectedColumns } from '@bingads-webui-component-legacy/bingads-grid/service/legacy-column-chooser-preferences';
import { LegacyGridFlag } from '@bingads-webui-component-legacy/bingads-grid/service/legacy-grid-flag-preferences';
import InlineSharedBudgetEditor from '@bingads-webui-campaign-legacy/shared-budget/view/campaigns-grid-inline-shared-budget-editor';
import InlineBudgetEditor from '@bingads-webui-campaign-legacy/budget/view/inline-budget-editor';
import jsData from '@bingads-webui-component-legacy/js-data-campaign/index';
import * as i18nErrors from '@bingads-webui-component-legacy/api-errors-campaign/index';

import { CampaignIdColumn } from './attributes/campaign-id';
import { CampaignLanguagesColumn } from './attributes/campaign-languages';
import { CampaignTypeColumn } from './attributes/campaign-type';
import { CampaignColumn } from './attributes/campaign';
import { StatusIconColumn } from './attributes/status-icon';
import { BidStrategyTargetCPAColumn } from './attributes/bid-strategy-target-cpa';
import { BidStrategyTargetROASColumn } from './attributes/bid-strategy-target-roas';
import { BidStrategyManualCPIColumn } from './attributes/bid-strategy-manual-cpi';
import { BidStrategyManualCPCColumn } from './attributes/bid-strategy-manual-cpc';
import { StartDateColumn } from './attributes/start-date';
import { EndDateColumn } from './attributes/end-date';
import { BidStrategyTargetImpressionShareColumn } from './attributes/bid-strategy-target-impression-share';
import { OptimizationScoreColumn } from './attributes/optimization-score';
import { PerformanceInsightsColumn } from './attributes/performance-insights';
import template from './attributes/budget.pug';

const Permissions = config.get('Permissions');
const i18n = window.globalLegacyI18n.get('@bingads-webui/i18n-amd-loader!campaignui/feature/campaigns-tab/campaigns');

// we use the shared version of delivery column to share code, but we need to keep the legacy name
// otherwise, user column chooser preference will break
class DeliveryColumnForCampaign extends DeliveryColumn {
  constructor(...args) {
    super(...args);

    this.config.name = 'DeliveryStatus';
  }
}

const {
  IsViewThroughConversionEnabled,
  IsAudienceSOVEnabled,
  IsAvgPositionDeprecationPhase1Enabled,
  IsAvgPositionDeprecationPhase2Enabled,
  IsPersonalizedOffersEnabled,
  IsPersonalizedOffersV2Enabled,
  IsPersonalizedOffersV3Enabled,
  IsViewThroughConversionsRevenueEnabled,
  IsPersonalizedOffersV4Enabled,
  IsOptimizationScoreInCampaignsGridEnabled,
  dynamic = {},
  IsPerformanceInsightsEnabled,
} = Permissions;
const {
  PartialConversions: IsPartialConversionsEnabled,
  ColumnConversion: IsColumnConversionEnabled,
  CPSReporting,
  InstallPerformanceColumns,
  ConvValueRules,
  CampaignLifetimeBudget,
  MaeLifetimeBudgetExclusion,
} = dynamic;
const usePartialConversions = IsPartialConversionsEnabled && IsColumnConversionEnabled;

const isPortfolioBidStrategyEnabled = _.result(Permissions.dynamic, 'PortfolioBidStrategy');
const isAppAdsCampaignPhaseOneEnabled = _.result(Permissions.dynamic, 'AppAdsCampaignPhaseOne');
const isUnifiedAppAdsEnabled = _.result(Permissions.dynamic, 'UnifiedAppAds');

const isCampaignLifetimeBudgetEnabled = CampaignLifetimeBudget && !MaeLifetimeBudgetExclusion;

function isCampaignTypeAllowed() {
  return Permissions.IsEnhancedShoppingCampaignAllowed ||
    Permissions.IsDynamicSearchAdsV1Enabled ||
    Permissions.IsAIMCampaignEnabled;
}

function getBuilder(options) {
  const builder = new ColumnsConfigBuilder({ Permissions });

  const getCampaignTypeGlobalFilter = options && options.getCampaignTypeGlobalFilter;

  builder
    .addCategory('Attributes', category => category.addColumns(_.compact([
      StatusIconColumn,
      CampaignColumn,
      BudgetColumn,
      isCampaignLifetimeBudgetEnabled && LifetimeBudgetColumn,
      IsOptimizationScoreInCampaignsGridEnabled ? OptimizationScoreColumn : null,
      IsPerformanceInsightsEnabled ? PerformanceInsightsColumn : null,
      DeliveryColumnForCampaign,
      CampaignIdColumn,
      isCampaignTypeAllowed() ? CampaignTypeColumn : null,
      isPortfolioBidStrategyEnabled && BidStrategyNameColumn,
      BidStrategyTypeColumn,
      isCampaignLifetimeBudgetEnabled && StartDateColumn,
      isCampaignLifetimeBudgetEnabled && EndDateColumn,
      BidStrategyTargetCPAColumn,
      isAppAdsCampaignPhaseOneEnabled && BidStrategyManualCPIColumn,
      isUnifiedAppAdsEnabled && BidStrategyManualCPCColumn,
      BidStrategyTargetROASColumn,
      BidStrategyTargetImpressionShareColumn,
      QualityScoreColumn,
      CampaignLanguagesColumn,
      LabelsColumn,
      IsPersonalizedOffersEnabled && !IsPersonalizedOffersV4Enabled
        ? CampaignCashbackScopeColumn : null,
      IsPersonalizedOffersEnabled && !IsPersonalizedOffersV4Enabled &&
        !(IsPersonalizedOffersV2Enabled || IsPersonalizedOffersV3Enabled) ?
        CashbackBudgetColumn : null,
      IsPersonalizedOffersEnabled && !IsPersonalizedOffersV4Enabled ?
        CampaignCashbackPercentColumn : null,
      IsPersonalizedOffersV3Enabled && !IsPersonalizedOffersV4Enabled ? CampaignCouponColumn : null,
    ])))
    .addPerformanceCategoryForCampaign({
      withFluctuations: true,
      IsAvgPositionDeprecationPhase2Enabled,
      getCampaignTypeGlobalFilter,
    })
    .addConversionsCategory({
      withFluctuations: true,
      withPartialConversions: IsPartialConversionsEnabled,
      IsViewThroughConversionEnabled,
      IsViewThroughConversionsRevenueEnabled,
      IsColumnConversionEnabled,
      isCPSEnabled: CPSReporting,
      isInstallColumnsEnabled: InstallPerformanceColumns,
      isConversionValueRuleColumnsEnabled: ConvValueRules,
    })
    .addCompetitiveCategoryForCampaignOrAdGroups({
      IsAudienceSOVEnabled,
    });

  if (Permissions.IsCallMeteringAllowed) {
    builder.addCallDetailsCategoryForCampaigns();
  }

  builder
    .setLockedColumns(_.compact([
      StatusIconColumn,
      CampaignColumn,
    ]))
    .setSelectedColumns(_.compact([
      StatusIconColumn,
      CampaignColumn,
      BudgetColumn,
      isCampaignLifetimeBudgetEnabled && LifetimeBudgetColumn,
      IsOptimizationScoreInCampaignsGridEnabled ? OptimizationScoreColumn : null,
      IsPerformanceInsightsEnabled ? PerformanceInsightsColumn : null,
      DeliveryColumnForCampaign,
      isCampaignTypeAllowed() ? CampaignTypeColumn : null,
      isPortfolioBidStrategyEnabled && BidStrategyNameColumn,
      BidStrategyTypeColumn,
      isCampaignLifetimeBudgetEnabled && StartDateColumn,
      isCampaignLifetimeBudgetEnabled && EndDateColumn,
      BidStrategyTargetCPAColumn,
      isAppAdsCampaignPhaseOneEnabled && BidStrategyManualCPIColumn,
      isUnifiedAppAdsEnabled && BidStrategyManualCPCColumn,
      BidStrategyTargetROASColumn,
      BidStrategyTargetImpressionShareColumn,
      LabelsColumn,
      ColumnClicksWithFluctuation,
      ColumnImpressionsWithFluctuation,
      ColumnCTR,
      ColumnAverageCPC,
      ColumnSpendWithFluctuation,
      usePartialConversions && ColumnConversionsCredit,
      !usePartialConversions && ColumnConversions,
      usePartialConversions && ColumnViewThroughConversionsCredit,
      !usePartialConversions && ColumnViewThroughConversions,
      !IsAvgPositionDeprecationPhase1Enabled
      && !IsAvgPositionDeprecationPhase2Enabled
      && ColumnAveragePosition,
      IsAvgPositionDeprecationPhase1Enabled && ColumnTopImpressionRate,
      IsAvgPositionDeprecationPhase1Enabled && ColumnAbsoluteTopImpressionRate,
    ]));

  return builder;
}

export function getColumnsConfig(options) {
  _.extend(options, {
    InlineSharedBudgetEditor,
    InlineBudgetEditor,
    budgetTemplate: template,
    decimal,
    permissions: Permissions,
    isCampaign: true,
    showExperiment: Permissions.IsExperimentsEnabled,
  });

  const getCampaignTypeGlobalFilter = options && options.getCampaignTypeGlobalFilter;
  return getBuilder({ getCampaignTypeGlobalFilter }).getColumnsConfig(options);
}

export function getColumnChooserConfig({ filterService, isForVNext = false, getCampaignTypeGlobalFilter }) {
  let customColumnConfig;
  if (Permissions.IsCustomColumnsInCampaignsSupported) {
    const { metricCategories, segmentCategories } = campaignMetricCategoriesFactory(Permissions);
    customColumnConfig = {
      enabled: true,
      metricCategories,
      segmentCategories,
      filterService,
    };
  }

  return _.defaults(getBuilder({ getCampaignTypeGlobalFilter }).getColumnChooserConfig({
    patchQuery: true,
    Columns,
    SelectedColumns,
    LegacyColumns,
    LegacySelectedColumns,
    LegacyGridFlag,
    i18n,
    CustomColumns,
    customColumnConfig,
    permissions: Permissions,
  }), {
    isForVNext,
    i18nErrors,
    AdCustomizerAttribute: jsData.AdCustomizerAttribute,
    resource: jsData.CampaignAdCustomizerAttribute,
    RSAAdCustomizerAttributeColumn,
    enableRSAAdCustomizer: true,
  });
}
